import React, { useState, useEffect, useCallback } from 'react';
import Pagination from '../Pagination';
import axios from '../../api/api';
import { useParams } from 'react-router-dom'
import { MdOutlineBlock } from 'react-icons/md';
import Loader from '../Loader';
import * as XLSX from "xlsx";

const ClientSuccessful = () => {

  const { id } = useParams()
  const [successfulRequests, setSuccessfulRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [count, setCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [service_id, setService_id] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Convert date to partition format (pYYYYMMDD)
  const formatDateToPartition = (date) => {
    const d = new Date(date);
    return `p${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };

  // Fetch successful reports
  const getSuccessfulReports = useCallback(async (offset, limit, email) => {
    try {
      let query = `&offset=${offset}&limit=${limit}`

      if (service_id) {
        query += `&service_id=${service_id}`
      }

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Check if endDate is before startDate
        if (end < start) {
          alert('End date cannot be earlier than start date');
          setLoading(false);
          return;
        }
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 32) {
          alert('Date range cannot exceed 32 days');
          setLoading(false);
          return;
        }

        const partitions = [];
        let current = new Date(startDate);

        // Handle case when start and end are the same
        if (diffDays === 0) {
          let current = new Date(startDate);
          current.setDate(current.getDate() + 1);
          partitions.push(formatDateToPartition(current));
        } else {
          while (current <= end) {
            current.setDate(current.getDate() + 1);
            partitions.push(formatDateToPartition(current));
          }
        }
        query += `&partitions=${partitions.join(',')}`;
      } else if (startDate) {
        const partitions = [];
        let current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        partitions.push(formatDateToPartition(current));
        query += `&partitions=${partitions.join(',')}`;
      }

      const response = await axios.get(`/request_log/archive?client_ref_id=${id}${query}`);
      setSuccessfulRequests(response.data.items);
      setCount(response.data.count)
      setTotal(response.data.total)
      setLoading(false);
      setError(null);
    } catch (error) {
      console.log(error);
      setLoading(false);
      if (!error.response) {
        setError("Network error! Check your connection.");
      } else if (error.response.status) {
        setError(error.response.data.detail)
      } else if (error.response.status >= 500) {
        setError("Server error! Please try again later.");
      } else {
        setError("An unexpected error occurred!");
      }
    }
  }, [service_id, startDate, endDate, id]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setLoading(true);
      getSuccessfulReports((currentPage - 1) * recordsPerPage, recordsPerPage);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [currentPage, recordsPerPage, getSuccessfulReports]);


  const handleExport = () => {
    const workbook = XLSX.utils.book_new();
    const excelData = successfulRequests.map((row) => {
      return {
        "ID": row.id,
        "MSISDN": row.msisdn,
        "Amount": row.amount,
        "Provider": row.provider,
        "Narration": row.Narration,
        "Transaction Date": new Date(row.trans_date).toLocaleString("en-CA", { hour12: false }),
        "Created Date": new Date(row.date_created).toLocaleString("en-CA", { hour12: false }), 
      };
    });
  
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "ClientSuccessful Requests");
    XLSX.writeFile(workbook, "successful_requests.xlsx");
  };


  return (
    <div className='my-2'>
      <div className='bg-white rounded-lg p-4 w-full flex-1 min-w-0'>
        <div className="flex items-center justify-between py-2">
          <div className='flex space-x-3 items-center'>
            <div>
              <select
                className='px-3 py-1 border bg-[#f2f9ff] border-slate-300 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277]'
                value={service_id}
                onChange={(e) => {setService_id(e.target.value)}}
              >
                <option value=''>Service Type</option>
                <option value='2'>Airtime</option>
                <option value='5'>Bundle</option>
              </select>
            </div>
          </div>
          <div>
            <span className='font-bold text-[#165277]'> Showing {count} / {total} records</span>
          </div>
          <div className='flex space-x-2'>
            <button className='bg-[#134463] text-white px-4 py-1 rounded-lg hover:bg-[#165277]' onClick={handleExport}>Export</button>
            <form className='flex space-x-3 items-center'>
              <label htmlFor="">
                <span className='hidden'>Start Date</span>
                <input 
                  type="date"
                  className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
                  value={startDate}
                  onChange={(e) => {setStartDate(e.target.value)}}
                />
              </label>
              <label htmlFor="">
                <span className='hidden'>End Date</span>
                <input 
                  type="date" 
                  className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
                  value={endDate}
                  onChange={(e) => {setEndDate(e.target.value)}}
                />
              </label>
            </form>
          </div>
        </div>
        {loading ? (
          <div className='flex items-center justify-center bg-[#e9f3f9] h-[50vh]'>
            <Loader />
          </div>
        ) : error ? (
          <div className='bg-[#e9f3f9] grid place-items-center h-[50vh]'>
            <div className='grid place-items-center text-red-600 p-4'>
              <h3><MdOutlineBlock /></h3>
              <span>{error}</span>
            </div>
          </div>
        ) : (
          <div className='overflow-x-auto rounded-lg'>
            {successfulRequests.length > 0
              ? (
                <table className='w-full text-left table-auto'>
                  <thead>
                    <tr className='border-b border-slate-500'>
                      <th className='p-2'>Phone Number</th>
                      <th className='p-2'>Account Number</th>
                      <th className='p-2'>Amount</th>
                      <th className='p-2'>Provider</th>
                      <th className='p-2'>Narration</th>
                      <th className='p-2'>Transaction Date</th>
                      <th className='p-2'>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {successfulRequests.map((request) => (
                      <tr key={request.id}>
                        <td className='p-2'>{request.msisdn}</td>
                        <td className='p-2'>{request.account_no}</td>
                        <td className='p-2'>{request.amount}</td>
                        <td className='p-2'>{request.provider}</td>
                        <td className='p-2'>{request.narration}</td>
                        <td className='p-2'>{new Date(request.tran_date).toLocaleString("en-CA", { hour12: false })}</td>
                        <td className='p-2'>{new Date(request.date_created).toLocaleString("en-CA", { hour12: false })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className='bg-[#e9f3f9] text-[#134463] grid place-items-center h-[50vh]'>
                  <div className='grid place-items-center p-4'>
                    <h3><MdOutlineBlock /></h3>
                    <h4>No Data</h4>
                  </div>
                </div>
              )}
          </div>
        )}
        <div className='flex flex-wrap items-center justify-between mt-4 gap-4'>
          <div>
            <span className='mr-2'>Records per page:</span>
            <select
              className='px-3 py-1 border bg-[#e9f3f9] border-slate-300 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
            >
              <option value='20'>20</option>
              <option value='50'>50</option>
              <option value='75'>75</option>
              <option value='100'>100</option>
            </select>
          </div>
          <Pagination
            nPages={Math.ceil(total / recordsPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientSuccessful;
